import * as THREE from 'three';
import { G, mesh, scene, stdMat } from './scene';
import { texRing } from './vfx-tex';
import { texNeedle, texPolyBand } from './vfx-tex2';
/* ============================================================
   常駐掛載的造型：硬邊幾何版
   ============================================================

   現行的 `gadgets.ts` 是「極簡霓虹幾何」——深色件 + 一條**加法混色的
   發光邊**。那一版跟第二版武器特效是同一輪做的，而第二版正是被
   「泛光太重像魔法」否決的那個方向：發光邊在泛光下會糊成一條光帶，
   於是旋刃讀成「一圈旋轉的光」而不是「三片刀」。

   這一版跟著武器特效走 A 方向，三條規則完全一樣：

   1. **零加法混色。** 亮的部分要嘛是高固有色的鋼（吃光影的實體），
      要嘛是暗色描邊包白心的平片（特效）—— 兩者都不用加法混色。
   2. **輪廓要銳利。** 俯視鏡頭下只看得到外框，外框糊掉就什麼都不是。
   3. **範圍標記用硬邊多邊形帶**，不是細線圓環。厚帶讀成「這一圈是我的」，
      細線讀成 UI。

   ## 實體與特效的分界（使用者 2026-08-05 拍板）

   | 掛載 | 走哪一邊 |
   |---|---|
   | 環繞旋刃 | **特效**：它是一道光刃，不是機甲配件 |
   | 感應無人機 | **實體**：它是一台機器，該吃光影 |
   | 斥力力場 | **特效**：地面上的硬邊帶 |

   判準是「這東西在故事上是不是一個物件」。無人機是，光刃不是 ——
   而第一版我把光刃做成鋼製楔形刀，於是畫面上一邊是白色硬邊光形、
   一邊是灰色金屬零件，讀起來像兩個系統各做各的。

   常駐物件不能用投射物池（池是借了要還的，這些的壽命跟槽位一樣長），
   所以各自持有 Group，由武裝的 `onEquip` 建立、`onRemove` 收掉。 */

/** 鋼：亮的固有色 + 低粗糙度。**這是「發光」的替代品** ——
    在冷夜的場景裡，一塊 0.86 亮度的鋼本來就是畫面上最亮的東西。 */
function steel() { return stdMat(0xdce4ee, { metal: 0.55, rough: 0.38, flat: true }); }
/** 暗件：讓亮面有東西可以對比。沒有暗部的話整台會糊成一片白。 */
function dark() { return stdMat(0x1b2331, { metal: 0.4, rough: 0.7, flat: true }); }

/** 範圍標記：躺平的硬邊六角厚帶。 */
function ringMark(op: number) {
  var m = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), new THREE.MeshBasicMaterial({
    map: texPolyBand(6, 0.055) as any, color: 0xdce8f2, transparent: true, opacity: op,
    depthWrite: false, side: THREE.DoubleSide
  }));
  m.rotation.x = -Math.PI / 2;
  return m;
}

/** 一片光刃：暗色描邊 + 白心的兩端收尖薄片，躺平在腰高。

    ⚠️ **它是特效不是機甲配件**（使用者 2026-08-05）。做成鋼製楔形刀的話，
    畫面上一邊是白色硬邊光形、一邊是灰色金屬零件，讀起來像兩個系統。

    **`depthTest` 一定要開。** 關掉的話整片刃畫在所有東西上面，敵人從刃
    「底下」穿過去 —— 使用者的回報是「刀片好像不會造成傷害了」，
    而傷害其實一直在，壞掉的是接觸感。 */
function lightBlade() {
  var g = new THREE.Group();
  var quad = new THREE.PlaneGeometry(1, 1);
  /* 描邊在下、白心在上，兩片差 0.03 公尺是為了避開同高度的 z-fighting。 */
  var ink = new THREE.Mesh(quad, new THREE.MeshBasicMaterial({
    map: texNeedle() as any, color: 0x151c26, transparent: true, opacity: 0.9,
    depthWrite: false, side: THREE.DoubleSide
  }));
  ink.rotation.x = -Math.PI / 2; ink.scale.set(6.0, 1.15, 1);
  var lit: any = new THREE.Mesh(quad, new THREE.MeshBasicMaterial({
    map: texNeedle() as any, color: 0xffffff, transparent: true, opacity: 1,
    depthWrite: false, side: THREE.DoubleSide
  }));
  /* 長寬比刻意拉開：寬度接近判定的話會讀成一片葉子，細長才讀成刃。
     面積的真話由扇形帶（`texArc`）講，這一片只負責「它在轉」。 */
  lit.rotation.x = -Math.PI / 2; lit.scale.set(5.4, 0.8, 1);
  lit.position.y = 0.03; lit.renderOrder = 5;
  g.add(ink); g.add(lit);
  return g;
}

/** 環繞旋刃：n 道光刃 + 一圈細的範圍框。

    ⚠️ 這裡試過一版「把判定整塊畫出來」（扇形環帶），使用者否決：**太醜**。
    所以回到細框 + 刀刃，但**框的半徑改成照實際判定圈畫**——
    那是使用者自己量到的問題：舊版用六角帶（`polyR(6,θ)*0.96`），
    邊中點只有 0.83R、頂點才 0.96R，**框比判定圈小 4–17%**，
    刀刃因此在自己的框外面掃。圓框沒有這個誤差。

    ⚠️ **還沒解的**：`w-orbit` 的判定是「角差 < 0.55 弧度」，一片刀只覆蓋
    整圈 17.5%，而 Lv1–2 只有一片刀 —— 敵人大部分是真的直接穿過去。
    細框畫不出那件事（畫得出來的那一版被否了），所以那是**玩法要調的**：
    放寬 0.55，或 Lv1 就給兩片。 */
export function makeOrbitBladesGeo(n: number, opt?: { span?: number; width?: number }) {
  var width = (opt && opt.width) || 1.6;
  var g = new THREE.Group();
  var blades: any[] = [];
  for (var i = 0; i < n; i++) {
    var pivot = new THREE.Group();
    var b = lightBlade();
    b.position.set(0, 1.5, 0);
    pivot.add(b);
    g.add(pivot);
    blades.push(pivot);
  }
  /* 範圍框：**圓的，不是六角**。六角是「人造物標記」的語言（軌道光柱的
     落點還在用），但這一圈標的是一個**圓形**的判定 —— 形狀不一樣就一定
     有誤差，而誤差正好落在「刀在框外掃」這個最刺眼的方向。 */
  var ring: any = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), new THREE.MeshBasicMaterial({
    map: texRing(0.03) as any, color: 0xdce8f2, transparent: true, opacity: 0.22,
    depthWrite: false, side: THREE.DoubleSide
  }));
  ring.rotation.x = -Math.PI / 2; ring.position.y = 0.13;
  g.add(ring);
  scene.add(g);
  return { g: g, blades: blades, ring: ring, width: width, mat: steel() };
}

/** 每幀設定半徑。刀刃騎在 R 上，框也畫在 R 上。

    `texRing` 的外緣在貼圖的 0.97，所以縮放要除回去 —— 少了這一步，
    框會比判定圈小 3%，又回到「刀在框外掃」的那個症狀（只是小一點）。 */
export function setBladeRadiusGeo(vis: any, R: number) {
  if (!vis) return;
  if (vis.ring) vis.ring.scale.setScalar(R / 0.97);
  for (var b = 0; b < vis.blades.length; b++) {
    var blade = vis.blades[b].children[vis.blades[b].children.length - 1];
    blade.position.set(0, 1.5, R);
  }
}

/** 斥力／吸力力場：**一圈**自轉的硬邊六角帶。

    ⚠️ 原本是兩圈（外圈 + 0.82 倍的內圈）。使用者 2026-08-05：
    「引力那招的地板目前有三個六邊形，拿掉一個，太多了」——
    那三個是這裡的兩圈，加上每 0.42 秒一次的傷害脈衝（`fxShockRing`）。

    刪掉的是**內圈**：它畫在 0.82R，而判定只有一個半徑 R
    （`weapons-pool.ts` 的 `d > R + e.r`）—— 那一圈不對應任何東西，
    純裝飾，而且它離外圈夠近，兩條線在俯視鏡頭下會互相干擾。
    「會轉的才讀成力場」那個理由由留下來的這一圈自己承擔。 */
export function makeFieldRingGeo() {
  var g = new THREE.Group();
  var a = ringMark(0.22); a.position.y = 0.14;
  g.add(a);
  scene.add(g);
  return { g: g, a: a, b: null as any, t: 0 };
}

/** 每幀。`R` 是當下半徑。呼吸只動不透明度不動尺寸 ——
    尺寸會動的話玩家讀不到「範圍到哪」。 */
export function updateFieldRingGeo(f: any, x: number, z: number, R: number, dt: number) {
  if (!f) return;
  f.t += dt;
  f.g.position.set(x, 0, z);
  f.a.scale.setScalar(R); f.a.rotation.z += dt * 0.5;
  var k = 0.78 + 0.22 * Math.sin(f.t * 2.4);
  f.a.material.opacity = 0.22 * k;
  /* `b` 是舊版的內圈，已經拿掉；欄位留著是因為呼叫端可能還在讀它。 */
  if (f.b) { f.b.scale.setScalar(R * 0.82); f.b.material.opacity = 0.12 * k; }
}

/** 感應無人機：六角盤 + 中央核心 + 一根有角度的砲管。

    砲管那一條線直接告訴玩家「它正在瞄哪邊」—— 俯視鏡頭下，
    一個分不出朝向的物件等於沒有資訊。 */
export function makeDroneGeo() {
  var g = new THREE.Group();
  var HEX = new THREE.CylinderGeometry(0.5, 0.5, 1, 6);
  var st = steel(), dk = dark();
  g.add(mesh(HEX, dk, [1.55, 0.26, 1.55], [0, 0, 0]));            // 盤（暗）
  g.add(mesh(HEX, st, [1.15, 0.30, 1.15], [0, 0.05, 0]));         // 上蓋（亮）
  g.add(mesh(HEX, st, [0.40, 0.46, 0.40], [0, 0.20, 0]));         // 核心
  g.add(mesh(G.box, dk, [0.22, 0.18, 1.05], [0, 0, 0.72]));       // 砲管
  g.add(mesh(G.cone, st, [0.20, 0.42, 0.20], [0, 0, 1.32], [Math.PI / 2, 0, 0]));  // 砲口
  var muzzle = mesh(G.box, st, [0.30, 0.24, 0.30], [0, 0, 1.25]);
  muzzle.visible = false;
  g.add(muzzle);
  scene.add(g);
  return { g: g, muzzle: muzzle };
}
